import { Button, ButtonGroup, IconButton, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type GeoImage, OrganizationAccessLevel, type Stored } from "@overmap-ai/common"
import { selectGeoImagesOfProject } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, useCallback, useMemo, useState } from "react"
import type { DateRange } from "react-day-picker"

import { GeoImageCard } from "../../components/Cards/GeoImageCard"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { GeoImageUploadDialog } from "../../components/Dialogs/GeoImageUploadDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { DayPickerPopover } from "../../components/Popovers/DayPickerPopover"
import { interactiveCardCva } from "../../cva"
import { usePagination } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"
import { formatDateRange } from "../../utils/date"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/images/")

const ITEMS_PER_PAGE = 15

export const GeoImagesView = memo(() => {
	const { projectId } = routeApi.useParams()
	const [dateRange, setDateRange] = useState<DateRange | undefined>()

	const geoImages = useShallowEqaulSelector(selectGeoImagesOfProject(projectId))

	// TODO: Filtering and sorting can be optimized here
	//  1) first sort by date
	//  2) then find the index of the first image that is within the date range and index of last image in date range
	// 	3) slice the array from first to last index
	const sortedGeoImages = useMemo(() => {
		const filteredImages = dateRange
			? geoImages.filter((img) => {
					if (!img.original_date) return false
					const imgDate = new Date(img.original_date)
					const fromDate = dateRange.from ? new Date(dateRange.from) : null
					const toDate = dateRange.to ? new Date(dateRange.to) : null

					if (fromDate && imgDate < fromDate) return false
					if (toDate) {
						// Set to end of day for inclusive comparison
						toDate.setHours(23, 59, 59, 999)
						if (imgDate > toDate) return false
					}
					return true
				})
			: geoImages

		return filteredImages.toSorted((a, b) => {
			if (!a.original_date) return 1
			if (!b.original_date) return -1
			return new Date(b.original_date).getTime() - new Date(a.original_date).getTime()
		})
	}, [geoImages, dateRange])

	const { page, pageCount, currentPage, nextPage, lastPage, firstPage, prevPage } = usePagination<Stored<GeoImage>>(
		sortedGeoImages,
		ITEMS_PER_PAGE,
	)

	const handleClearDateRange = useCallback(() => {
		setDateRange(undefined)
	}, [])

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col justify-center gap-4 overflow-hidden">
				<ButtonGroup
					className="flex items-center justify-between gap-2"
					variant="soft"
					accentColor="base"
					size="sm"
				>
					<div className="flex items-center gap-2">
						<DayPickerPopover
							mode="range"
							required={false}
							selected={dateRange}
							onSelect={setDateRange}
							variant="solid"
							align="start"
							size="sm"
						>
							<Button className="w-max">
								<LuIcon icon={modelIconMapping.filter} />
								{dateRange ? formatDateRange(dateRange) : "Filter"}
							</Button>
						</DayPickerPopover>
						{!!dateRange && (
							<IconButton onClick={handleClearDateRange}>
								<LuIcon icon={modelIconMapping.close} />
							</IconButton>
						)}
					</div>

					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
						<GeoImageUploadDialog size="sm">
							<Button variant="surface" accentColor="primary" size="sm">
								<LuIcon icon={modelIconMapping.add} />
								Add
							</Button>
						</GeoImageUploadDialog>
					</OrganizationAccessRequired>
				</ButtonGroup>
				<Separator size="full" />
				<div className="grow overflow-y-auto">
					{page.length > 0 ? (
						<div className="grid h-max grid-cols-1 gap-2 overflow-y-auto md:grid-cols-2 lg:grid-cols-3">
							{page.map((geoImage) => (
								<GeoImageCard
									geoImageId={geoImage.uuid}
									key={geoImage.uuid}
									className={cx(interactiveCardCva(), "h-[300px] cursor-pointer")}
								/>
							))}
						</div>
					) : (
						<div className="flex size-full items-center justify-center">
							<Text accentColor="base" size="sm">
								No images found
							</Text>
						</div>
					)}
				</div>
				<Separator size="full" />
				<div className="flex h-max justify-between">
					<Text accentColor="base" size="sm">
						Page <Text highContrast>{currentPage + 1}</Text> of <Text highContrast>{pageCount || 1}</Text>
					</Text>
					<ButtonGroup className="flex items-center" size="sm" accentColor="base" variant="ghost">
						<IconButton
							onClick={firstPage}
							aria-label="first-page"
							disabled={pageCount === 0 || currentPage === 0}
						>
							<LuIcon icon={modelIconMapping.chevronsLeft} />
						</IconButton>
						<IconButton
							onClick={prevPage}
							aria-label="previous-page"
							disabled={pageCount === 0 || currentPage === 0}
						>
							<LuIcon icon={modelIconMapping.chevronLeft} />
						</IconButton>
						<IconButton
							onClick={nextPage}
							aria-label="next-page"
							disabled={pageCount === 0 || currentPage === pageCount - 1}
						>
							<LuIcon icon={modelIconMapping.chevronRight} />
						</IconButton>
						<IconButton
							onClick={lastPage}
							aria-label="last-page"
							disabled={pageCount === 0 || currentPage === pageCount - 1}
						>
							<LuIcon icon={modelIconMapping.chevronsRight} />
						</IconButton>
					</ButtonGroup>
				</div>
			</div>
		</ViewContainer>
	)
})
GeoImagesView.displayName = "GeoImagesView"
